import React, { useState } from "react";
import { Trash, CheckSquare, X } from "@phosphor-icons/react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

function ConfirmDelete({ open, onOpenChange, rules, onConfirm }) {
  const drafts = rules.filter((r) => r.status === "draft").length;
  const live = rules.length - drafts;
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="bg-[#0F0F0F] border-neutral-800 text-neutral-200" data-testid="bulk-delete-confirm">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-heading text-white">Delete {plural(rules.length, "rule", "rules")}?</AlertDialogTitle>
          <AlertDialogDescription className="text-neutral-400" data-testid="bulk-delete-summary">
            This removes {plural(live, "approved rule", "approved rules")} and {plural(drafts, "draft", "drafts")}.
            {live > 0 && " Approved rules stop running from the next sync."} This can't be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <ul className="max-h-48 overflow-y-auto rounded-lg border border-neutral-800 divide-y divide-neutral-800/70 text-xs" data-testid="bulk-delete-list">
          {rules.map((r) => (
            <li key={r.id} className="px-3 py-2 flex items-center gap-2">
              <span className="truncate text-neutral-200">{r.title}</span>
              {r.status === "draft" && <span className="ml-auto shrink-0 text-[0.6rem] font-bold uppercase tracking-wider text-amber-300">Draft</span>}
            </li>
          ))}
        </ul>
        <AlertDialogFooter>
          <AlertDialogCancel className="bg-transparent border-neutral-700 text-neutral-200 hover:bg-white/5 hover:text-white" data-testid="bulk-delete-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} className="bg-red-500 text-white hover:bg-red-600" data-testid="bulk-delete-confirm-btn">
            Delete {plural(rules.length, "rule", "rules")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function SelectionBar({ selectedRules, shownCount, onSelectAll, onClear, onDelete, onDone }) {
  const [confirm, setConfirm] = useState(false);
  const n = selectedRules.length;
  return (
    <div className="sticky top-2 z-20 mb-6 flex flex-wrap items-center gap-2 rounded-2xl border border-[#73bf44]/30 bg-[#0F0F0F]/90 backdrop-blur-md px-4 py-3" data-testid="rules-selection-bar">
      <span className="text-sm font-semibold text-white mr-2" data-testid="rules-selected-count">{n} selected</span>
      <button onClick={onSelectAll} disabled={!shownCount} data-testid="rules-select-all" className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-neutral-700 text-neutral-300 hover:text-white hover:border-neutral-500 disabled:opacity-40">
        <CheckSquare size={13} /> Select all shown · {shownCount}
      </button>
      <button onClick={onClear} disabled={!n} data-testid="rules-select-clear" className="text-xs px-3 py-1.5 rounded-full text-neutral-400 hover:text-white disabled:opacity-40">Clear</button>
      <div className="ml-auto flex items-center gap-2">
        <button onClick={() => setConfirm(true)} disabled={!n} data-testid="rules-delete-selected" className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full bg-red-500/90 text-white hover:bg-red-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
          <Trash size={15} /> Delete selected
        </button>
        <button onClick={onDone} title="Stop selecting" data-testid="rules-select-done" className="p-2 rounded-full text-neutral-400 hover:text-white hover:bg-white/[0.06]"><X size={15} /></button>
      </div>
      <ConfirmDelete open={confirm} onOpenChange={setConfirm} rules={selectedRules} onConfirm={() => onDelete(selectedRules)} />
    </div>
  );
}
